import React, { useState, useEffect, useRef } from 'react';
import { realtimeDatabase } from "../../database/firebase";
import { ref, set, onValue, push, update, remove, get, serverTimestamp } from 'firebase/database';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate, useLocation } from 'react-router-dom';
import './allusers.css';

const USERS_PER_PAGE = 8;

const AllUsers = ({ updateHeader }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [challengeInProgress, setChallengeInProgress] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [riddlerusLogo, setRiddlerusLogo] = useState("/img/logo.png");
  const navigate = useNavigate();
  const [isLogoSet, SetIsLogoSet] = useState(false);
  const location = useLocation();
  const [userData, setUserData] = useState(null);
  const [themeData, setThemeData] = useState(null);
  const [textcolor, Setcolorcode] = useState(null);
  const [boxcolor, SetBoxcolorcode] = useState(null);
  const [boxtextcolor, SetBoxTextcolor] = useState(null);
  const [Buttoncolor, SetButtoncolor] = useState(null);
  const [buttontextcolor, SetButtonTextcolor] = useState(null);
  const [userId, SetUserId] = useState(null);
  const [sessionId, SetSessionId] = useState(null);
  const [organizationId, SetOrganizationId] = useState(null);
  const [selectedBoardType, setSelectedBoardType] = useState(null);
  const [boardTypeModalOpen, setBoardTypeModalOpen] = useState(false);
  const [pendingChallenge, setPendingChallenge] = useState(null);

  const userSessionRef = useRef(null);
  const usersListenerRef = useRef(null);
  const challengeListenerRef = useRef(null);
  const roomInviteHandledRef = useRef(null);

  useEffect(() => {
    if (themeData) {
      const body = document.body;

      const backgroundImage = window.innerWidth <= 768 ? themeData.background_mob : themeData.background_desk;
      if (body.classList.contains('common-bg')) {
        body.style.backgroundImage = `url(${backgroundImage})`;
      }
      setRiddlerusLogo(themeData.logo);
      SetIsLogoSet(true);
    }
  }, [themeData]);

  useEffect(() => {

    updateHeader({
      backButtonRedirect: "/userpage",
      backButtonDisabled: false
    });

    const fetchData = async () => {
      const storedSession = sessionStorage.getItem("userData");
      console.log(storedSession);
      const storedTheme = sessionStorage.getItem("themeData");

      if (storedSession && storedTheme) {
        const parsedUser = JSON.parse(storedSession);
        const parsedTheme = JSON.parse(storedTheme);

        setUserData(parsedUser);
        SetUserId(parsedUser.userId);
        SetSessionId(parsedUser.sessionId);
        SetOrganizationId(parsedUser.organizationId);
        setThemeData(parsedTheme);
        Setcolorcode(parsedTheme.landing_page_title_color);
        SetBoxcolorcode(parsedTheme.box_color);
        SetBoxTextcolor(parsedTheme.box_text_color);
        SetButtonTextcolor(parsedTheme.button_text_color);
        SetButtoncolor(parsedTheme.button_color);
      } else {
        navigate("/login"); // Redirect if session data is missing
      }
    };

    fetchData();
  }, [navigate]);

  // Initialize user session in Firebase when on allusers page; set offline when leaving
  // Do NOT set status to "online" here - status is only updated when user accepts (playing) or leaves game (online)
  useEffect(() => {
    if (!userId || !sessionId) return;

    const userSessionReference = ref(realtimeDatabase, `sessions/${sessionId}/users/${userId}`);

    const initializeUserSession = async () => {
      const snapshot = await get(userSessionReference);
      if (!snapshot.exists()) {
        await set(userSessionReference, {
          islive: true,
          live: true,
          timestamp: serverTimestamp(),
          name: userData?.name || userData?.firstName || 'Unknown Player',
          firstName: userData?.firstName || '',
          lastName: userData?.lastName || '',
          email: userData?.email || ''
        });
      } else {
        await update(userSessionReference, { islive: true, live: true, timestamp: serverTimestamp() });
      }
    };

    initializeUserSession();

    // When user leaves allusers page, set islive: false so they don't appear in live list
    return () => {
      update(userSessionReference, { islive: false, live: false, timestamp: serverTimestamp() }).catch(err =>
        console.error('Error setting user offline on leave:', err)
      );
    };
  }, [userId, sessionId, userData, navigate]);

  // Listen to all users in session
  useEffect(() => {
    if (!sessionId) return;

    const sessionsRef = ref(realtimeDatabase, `sessions/${sessionId}/users/`);

    usersListenerRef.current = onValue(sessionsRef, (snapshot) => {
      if (snapshot.exists()) {
        const allUserData = snapshot.val();
        console.log("allUserData", allUserData);
        fetchUsers(allUserData);
      }
    });

    return () => {
      if (usersListenerRef.current) {
        usersListenerRef.current();
      }
    };
  }, [sessionId, searchTerm]);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Listen for challenges
  useEffect(() => {
    if (!userId || !sessionId) return;

    const userRef = ref(realtimeDatabase, `sessions/${sessionId}/users/${userId}`);

    const checkForChallenges = async () => {
      try {
        // Check current user's state for incoming challenges
        const userSnapshot = await get(userRef);
        if (userSnapshot.exists()) {
          const userData = userSnapshot.val();
          console.log('Checking challenges for user:', userId, 'Data:', userData);

          // Only show popup if current user has an incoming challenge request (not if they sent it)
          if (userData.challengeStatus === 'request' && userData.challengeFrom && userData.challengeFrom !== userId) {
            console.log('User has incoming challenge request from:', userData.challengeFrom);

            // Use the challenger name from the current user's data since it should already be set when challenge was sent
            let challengerName = userData.challengerName || 'Unknown Player';

            console.log('Value of userData.challengerName:', userData.challengerName); // Debug log
            console.log('Using challenger name from userData:', challengerName); // Debug log

            showChallengeDialog({
              player: userData.challengeFrom,
              opponentId: userId,
              challengeId: userData.challengeId,
              challengerName: challengerName,
              boardType: userData.boardType
            });
          }

          // Only show rejection notice if current user sent a challenge that was rejected
          if ((userData.challengeStatus === null || userData.status === 'online') && userData.challengeTo && userData.challengeTo !== userId) {
            console.log('User sent challenge was rejected to:', userData.challengeTo);
            showRejectionNotice();

            // Clear the challengeTo field
            await update(userRef, {
              challengeTo: null
            });
          }

          // Only navigate to game if current user's Challenge was accepted
          if (userData.challengeStatus === 'accepted' && userData.challengeTo) {
            console.log('User sent challenge was accepted to:', userData.challengeTo);

            // Store challengeId and current opponent info in sessionStorage so game page shows correct players (not stale from a previous challenge)
            const storedSession = sessionStorage.getItem("userData");
            if (storedSession) {
              const parsedUser = JSON.parse(storedSession);
              const updatedUser = {
                ...parsedUser,
                challengeId: userData.challengeId,
                gametype: 'live',
                challengeTo: userData.challengeTo,
                opponentId: userData.opponentId || userData.challengeTo,
                opponentName: userData.opponentName || '',
                opponentEmail: userData.opponentEmail || '',
                boardType: userData.boardType || 'single'
              };

              sessionStorage.setItem("userData", JSON.stringify(updatedUser));
              setUserData(updatedUser);
            }

            // Clear challenge status immediately to prevent duplicate navigation
            await update(userRef, {
              challengeStatus: null,
              challengeTo: null,
              challengeId: null
            });

            // Navigate to game
            navigate(`/game?gameId=${userData.challengeId}`);
          }

          // 2v2 room invite (from Invite People in game lobby) – accept/reject like 1v1
          const invite = userData.pendingRoomInvite;
          if (invite && invite.roomCode) {
            const inviteKey = `${invite.roomCode}-${invite.timestamp}`;
            if (roomInviteHandledRef.current === inviteKey) return;
            roomInviteHandledRef.current = inviteKey;

            Swal.fire({
              title: '2v2 Room Invite',
              html: `<strong>${invite.inviterName || 'Someone'}</strong> invited you to join 2v2 room <strong>${invite.roomCode}</strong>. Accept?`,
              icon: 'question',
              showCancelButton: true,
              confirmButtonText: 'Accept',
              cancelButtonText: 'Reject',
              background: '#1e1e2d'
            }).then(async (result) => {
              if (result.isConfirmed) {
                const storedSession = sessionStorage.getItem("userData");
                if (storedSession) {
                  const parsedUser = JSON.parse(storedSession);
                  const updatedUser = { ...parsedUser, gametype: 'live-2v2' };
                  sessionStorage.setItem("userData", JSON.stringify(updatedUser));
                  setUserData(updatedUser);
                }
                await update(userRef, { pendingRoomInvite: null });
                navigate(`/game?gameId=${invite.roomCode}`);
              } else {
                await update(userRef, { pendingRoomInvite: null });
                const inviterRef = ref(realtimeDatabase, `sessions/${sessionId}/users/${invite.inviterId}`);
                const ss = sessionStorage.getItem('userData');
                const rejectorName = ss ? (JSON.parse(ss).name || JSON.parse(ss).firstName || 'A player') : 'A player';
                await update(inviterRef, {
                  roomInviteRejected: {
                    by: userId,
                    byName: rejectorName,
                    roomCode: invite.roomCode,
                    timestamp: Date.now(),
                  },
                });
              }
            });
          }
        }
      } catch (error) {
        console.error('Error checking challenges:', error);
      }
    };

    challengeListenerRef.current = onValue(userRef, async (snapshot) => {
      if (snapshot.exists()) {
        console.log('User data updated, checking for challenges...');
        await checkForChallenges();
      }
    });

    return () => {
      if (challengeListenerRef.current) {
        challengeListenerRef.current();
      }
    };
  }, [userData]);

  const fetchUsers = async (allUserData) => {
    try {
      console.log('Processing users from Firebase data:', allUserData);

      // Convert Firebase object to array format
      const usersArray = Object.keys(allUserData).map(key => ({
        userid: key,
        ...allUserData[key]
      }));
      // Filter out current user. Show users who are on allusers (islive === true) OR in a game (status === 'playing' or 'Engaged') so they appear with Playing/Engaged disabled.
      let filteredUsers = usersArray.filter(user => user.userid !== userId && (user.islive === true || user.status === 'playing' || user.status === 'Engaged' || user.status === 'gameover'));

      // Apply search filter if search term exists
      if (searchTerm.trim()) {
        filteredUsers = filteredUsers.filter(user =>
          user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      console.log('Filtered users (should display):', filteredUsers);

      setUsers(filteredUsers);
      setCurrentPage(1);
      setLoading(false);
    } catch (error) {
      console.error('Error processing users data:', error);
      setUsers([]); // Set empty array on error
      setLoading(false);
    }
  };

  const handleChallengeResponse = async (challengerId, response) => {
    try {
      const userSnapshot = await get(ref(realtimeDatabase, `sessions/${sessionId}/users/${userId}`));
      if (userSnapshot.exists()) {
        const userData = userSnapshot.val();
        const challengeId = userData.challengeId;

        const updates = {};

        if (response === 'accepted') {
          // Update current user (receiver) to accepted and playing
          updates[`sessions/${sessionId}/users/${userId}/challengeStatus`] = 'accepted';
          updates[`sessions/${sessionId}/users/${userId}/status`] = 'playing'; // Set status to playing

          // Update challenger to accepted and playing so they get notified and navigate to game
          updates[`sessions/${sessionId}/users/${challengerId}/challengeStatus`] = 'accepted';
          updates[`sessions/${sessionId}/users/${challengerId}/status`] = 'playing'; // Set status to playing

          await update(ref(realtimeDatabase), updates);

          // Both users will navigate to game via their listeners
          navigate(`/game?gameId=${challengeId}`);
        } else {
          // Clear challenge states and reset status to online for both users
          updates[`sessions/${sessionId}/users/${userId}/challengeStatus`] = null;
          updates[`sessions/${sessionId}/users/${userId}/challengeFrom`] = null;
          updates[`sessions/${sessionId}/users/${userId}/challengeId`] = null;
          updates[`sessions/${sessionId}/users/${userId}/status`] = 'online'; // Reset status to online

          // For challenger: clear challengeStatus but keep challengeTo temporarily so they can detect rejection
          updates[`sessions/${sessionId}/users/${challengerId}/challengeStatus`] = null;
          updates[`sessions/${sessionId}/users/${challengerId}/status`] = 'online'; // Reset status to online
          // Don't clear challengeTo yet - let challenger detect rejection first

          await update(ref(realtimeDatabase), updates);
        }
      }
    } catch (error) {
      console.error('Error handling challenge response:', error);
    }
  };

  const sendChallenge = async (opponentUserId, opponentName, opponentEmail, buttonId, boardType) => {
    if (challengeInProgress) return;

    setChallengeInProgress(true);
    const button = document.getElementById(buttonId);
    button.disabled = true;
    button.textContent = 'Pending...';
    button.className = 'selectuser btn-warning';

    try {
      // Update Firebase with challenge request
      // Generate a unique challenge ID
      const timestamp = Date.now();
      const random = Math.random().toString(36).substr(2, 9);
      const challengeId = `challenge_${timestamp}_${random}`;

      // Update sender's state - status should be 'pending'
      const senderRef = ref(realtimeDatabase, `sessions/${sessionId}/users/${userId}`);
      await update(senderRef, {
        challengeStatus: 'pending',
        challengeTo: opponentUserId,
        challengeId: challengeId,
        opponentName: opponentName, // Add opponent name to sender's data
        opponentId: opponentUserId, // Add opponent userId to sender's data
        opponentEmail: opponentEmail, // Add opponent email to sender's data
        boardType: boardType, // Add board type to challenge
        status: 'pending', // Set status to pending
        timestamp: Date.now()
      });

      // Update opponent's state - status should be 'request'
      const opponentRef = ref(realtimeDatabase, `sessions/${sessionId}/users/${opponentUserId}`);
      await update(opponentRef, {
        challengeStatus: 'request',
        challengeFrom: userId,
        challengeId: challengeId,
        challengerName: userData?.name || userData?.firstName || 'Unknown Player', // Add challenger's name
        boardType: boardType, // Add board type to challenge
        status: 'request', // Set status to request
        timestamp: Date.now()
      });

      // Don't show popup to sender - only receiver gets popup via their listener

    } catch (error) {
      console.error('Error sending challenge:', error);
      button.disabled = false;
      button.textContent = 'Challenge';
      button.className = 'selectuser btn-secondary';
      setChallengeInProgress(false);
    }
  };

  const getBoardTypeLabel = (boardType) => {
    if (boardType === 'match') return 'Match (25 / 8)';
    return 'Single Game';
  };

  const showChallengeDialog = (challenger) => {
    console.log("challenger", challenger);
    const boardLabel = challenger.boardType ? getBoardTypeLabel(challenger.boardType) : 'Unknown';
    Swal.fire({
      title: 'Challenge Request!',
      html: `You've been challenged by <strong>${challenger.challengerName}</strong> for <strong>${boardLabel}</strong>. Accept challenge?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Accept',
      cancelButtonText: 'Reject',
      background: '#1e1e2d',
      allowOutsideClick: false
    }).then(async (result) => {
      const status = result.isConfirmed ? 'accepted' : 'rejected';
      const opponentId = challenger.player;
      const challengeId = challenger.challengeId;

      try {
        // Update both users' states in Firebase
        const updates = {};

        if (status === 'accepted') {
          // Store challengeId in sessionStorage and userData for receiver
          const storedSession = sessionStorage.getItem("userData");
          if (storedSession) {
            const parsedUser = JSON.parse(storedSession);
            const updatedUser = {
              ...parsedUser,
              challengeId: challengeId,
              gametype: '1v1', // Set game type for multiplayer
              challengeTo: opponentId,
              opponentName: challenger.challengerName,
              opponentEmail: challenger.email || '',
              boardType: challenger.boardType || 'single'
            };

            sessionStorage.setItem("userData", JSON.stringify(updatedUser));
            setUserData(updatedUser);
          }
          // Update challenger state
          updates[`sessions/${sessionId}/users/${userId}/challengeStatus`] = 'accepted';
          updates[`sessions/${sessionId}/users/${userId}/challengeTo`] = opponentId;
          updates[`sessions/${sessionId}/users/${userId}/status`] = 'playing'; // Set status to playing
          updates[`sessions/${sessionId}/users/${userId}/opponentName`] = challenger.challengerName; // Add opponent name for challenger
          updates[`sessions/${sessionId}/users/${userId}/opponentEmail`] = challenger.email || ''; // Add opponent email for challenger

          // Update opponent state
          updates[`sessions/${sessionId}/users/${opponentId}/challengeStatus`] = 'accepted';
          updates[`sessions/${sessionId}/users/${opponentId}/challengeFrom`] = userId;
          updates[`sessions/${sessionId}/users/${opponentId}/status`] = 'playing'; // Set status to playing
          updates[`sessions/${sessionId}/users/${opponentId}/opponentName`] = userData?.name || 'Unknown'; // Add opponent name for receiver
          updates[`sessions/${sessionId}/users/${opponentId}/opponentEmail`] = userData?.email || ''; // Add opponent email for receiver

          // Navigate to game
          await update(ref(realtimeDatabase), updates);
          navigate(`/game?gameId=${challengeId}`);
        } else {
          // Clear challenge states and reset status to online
          updates[`sessions/${sessionId}/users/${userId}/challengeStatus`] = null;
          updates[`sessions/${sessionId}/users/${userId}/challengeFrom`] = null;
          updates[`sessions/${sessionId}/users/${userId}/challengeId`] = null;
          updates[`sessions/${sessionId}/users/${userId}/status`] = 'online'; // Reset status to online

          // For challenger: clear challengeStatus but keep challengeTo temporarily so they can detect rejection
          updates[`sessions/${sessionId}/users/${opponentId}/challengeStatus`] = null;
          // Don't clear challengeTo yet - let challenger detect rejection first
          updates[`sessions/${sessionId}/users/${opponentId}/challengeId`] = null;
          updates[`sessions/${sessionId}/users/${opponentId}/status`] = 'online'; // Reset status to online

          // Remove board type from userData when challenge is rejected by receiver
          const updatedUserData = { ...userData };
          delete updatedUserData.boardType;
          setUserData(updatedUserData);
          sessionStorage.setItem("userData", JSON.stringify(updatedUserData));

          await update(ref(realtimeDatabase), updates);
        }
      } catch (error) {
        console.error('Error handling challenge:', error);
      }
    });
  };

  const showBoardTypeSelectionModal = (opponentUserId, opponentName, opponentEmail, buttonId) => {
    setPendingChallenge({ opponentUserId, opponentName, opponentEmail, buttonId });
    setSelectedBoardType(null);
    
    Swal.fire({
      title: 'Select Board Type',
      html: `
        <div style="text-align: left; padding: 20px;">
          <div style="margin-bottom: 20px;">
            <label style="display: block; margin-bottom: 10px; font-weight: bold; color: #333;">Choose Game Board Type:</label>
            <div id="boardTypeOptions" style="display: flex; flex-direction: column; gap: 10px;">
              <label style="display: flex; align-items: center; padding: 10px; border: 2px solid #ddd; border-radius: 5px; cursor: pointer; transition: all 0.3s;" data-value="single">
                <input type="radio" name="boardType" value="single" style="margin-right: 10px;">
                <span style="font-size: 16px;">Single Game</span>
              </label>
              <label style="display: flex; align-items: center; padding: 10px; border: 2px solid #ddd; border-radius: 5px; cursor: pointer; transition: all 0.3s;" data-value="match">
                <input type="radio" name="boardType" value="match" style="margin-right: 10px;">
                <span style="font-size: 16px;">Match (25 / 8)</span>
              </label>
            </div>
          </div>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'OK',
      cancelButtonText: 'Cancel',
      background: '#1e1e2d',
      preConfirm: () => {
        const selected = document.querySelector('input[name="boardType"]:checked');
        if (!selected) {
          Swal.showValidationMessage('Please select a board type');
          return false;
        }
        return selected.value;
      },
      didOpen: () => {
        // Add click handlers for visual selection
        const labels = document.querySelectorAll('#boardTypeOptions label');
        labels.forEach(label => {
          label.addEventListener('click', function() {
            // Remove previous selection styling
            labels.forEach(l => {
              l.style.borderColor = '#ddd';
              l.style.backgroundColor = '#f9f9f9';
            });
            // Add selection styling to clicked label
            this.style.borderColor = '#007bff';
            this.style.backgroundColor = '#e7f3ff';
            // Check the radio button
            this.querySelector('input[type="radio"]').checked = true;
          });
        });
      }
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        // Add board type to userData
        const updatedUserData = {
          ...userData,
          boardType: result.value
        };
        setUserData(updatedUserData);
        sessionStorage.setItem("userData", JSON.stringify(updatedUserData));
        
        // Send challenge with selected board type
        sendChallenge(opponentUserId, opponentName, opponentEmail, buttonId, result.value);
      }
      setPendingChallenge(null);
    });
  };

  const showRejectionNotice = () => {
    Swal.fire({
      title: 'Challenge Rejected',
      text: 'The other player declined your challenge.',
      icon: 'info',
      showConfirmButton: true,
      background: '#1e1e2d',
    }).then(() => {
      // Remove board type from userData when challenge is rejected
      const updatedUserData = { ...userData };
      delete updatedUserData.boardType;
      setUserData(updatedUserData);
      sessionStorage.setItem("userData", JSON.stringify(updatedUserData));
      
      setChallengeInProgress(false);
    });
  };



  const getButtonConfig = (user) => {
    // Check if current user has sent a pending challenge to this user
    if (user.challengeTo === user.userid && user.challengeStatus === 'pending') {
      return { text: 'Pending', disabled: true, class: 'btn-warning' };
    }

    // Check if this user has sent a challenge to current user (incoming request)
    if (user.challengeFrom === userId && user.challengeStatus === 'request') {
      return { text: 'Pending', disabled: true, class: 'btn-warning' };
    }

    // Check if user's game is over - show "Completed"
    if (user.status === 'gameover') {
      return { text: 'Completed', disabled: true, class: 'btn-success' };
    }

    // Check if user is playing - show "Playing" and disable button
    if (user.status === 'playing') {
      return { text: 'Playing', disabled: true, class: 'btn-danger' };
    }

    // Check if user is engaged - show "Engaged" and disable button
    if (user.status === 'Engaged') {
      return { text: 'Engaged', disabled: true, class: 'btn-warning' };
    }

    // Only show Challenge button if user status is online
    if (user.status === 'online') {
      return { text: 'Challenge', disabled: false, class: 'btn-secondary' };
    }

    // For any other status, show disabled button
    return { text: 'Busy', disabled: true, class: 'btn-secondary' };
  };

  const totalPages = Math.ceil(users.length / USERS_PER_PAGE) || 1;
  const startIndex = (currentPage - 1) * USERS_PER_PAGE;
  const paginatedUsers = users.slice(startIndex, startIndex + USERS_PER_PAGE);

  if (loading) {
    return (
      <div className="screen">
        <div className="middle-screen">
          <div className="waiting-screen">
            <div className="col-md-12">
              <div className="tournament-title">
                Loading...
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="screen" id="allusers">
      <div className="middle-screen">
        <div className="waiting-screen">
          <div className="col-md-12">
            <div className="tournament-title" style={{ color: textcolor }}>
              Live Users
            </div>
          </div>
          <div className="col-md-12 tournament-table">
            {/* Search input */}
            <div className="search-container">
              <label>Search: </label>
              <input
                type="text"
                placeholder="Search users by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <table className="table table-striped table-bordered table-show" id="tableId">
              <thead className="table-dark">
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody className="table-show-body">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="allusers-empty-state">
                      <div className="allusers-empty-message" style={{ color: textcolor || '#555' }}>
                        No other users have joined the game yet.
                      </div>
                      <div className="allusers-empty-hint" style={{ color: textcolor || '#888' }}>
                        Wait for others to join this session to challenge them.
                      </div>
                    </td>
                  </tr>
                ) : (
                  Array.isArray(paginatedUsers) && paginatedUsers.map((user) => {
                    const buttonConfig = getButtonConfig(user);
                    const buttonId = `completeBtn_${user.userid}`;

                    return (
                      <tr key={user.userid}>
                        <td>{user.name || '-'}</td>
                        <td>{user.email || '-'}</td>
                        <td>
                          <button
                            className={`selectuser ${buttonConfig.class}`}
                            id={buttonId}
                            disabled={buttonConfig.disabled || challengeInProgress}
                            onClick={() => {
                              if (!buttonConfig.disabled) {
                                showBoardTypeSelectionModal(user.userid, user.name, user.email, buttonId);
                              }
                            }}
                          >
                            {buttonConfig.text}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="pagination-controls" style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  className="selectuser btn-secondary"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span style={{ color: textcolor || '#333', fontSize: '14px' }}>
                  Page {currentPage} of {totalPages}
                  {users.length > 0 && ` (${startIndex + 1}-${Math.min(startIndex + USERS_PER_PAGE, users.length)} of ${users.length})`}
                </span>
                <button
                  className="selectuser btn-secondary"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            )}

          </div>
        </div>
      </div>

    </div>
  );
};

export default AllUsers;
